import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
} from 'react';
import ReactDOM from 'react-dom/client';
import { Backdrop, CircularProgress, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { loadFederatedModule } from '../../../utils/loadFederatedModule';
import { getFeatureFlag } from '../utils/getFeatureFlag';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { activityEditorAnalytics } from './activityEditorAnalytics';
import {
  showActivityEditorToast,
  type ActivityEditorToastEvent,
} from './showActivityEditorToast';
import { holdActivityOpen } from '../../okr/okrActivityStack';

type ActivityEditorCloseReason = 'dismiss' | 'deleted' | 'switch-to-legacy';
type ActivityEditorCloseInfo = { wasEdited: boolean };

// Shape of the workstreams remote's ActivityEditorDialog (WorkstreamsUI/ActivityEditorDialog).
type ActivityEditorRemoteProps = {
  activityId: string;
  baseApiUrl: string;
  currentUser: { id: number; fullname: string; profilePictureUrl: string };
  featureFlags: Record<string, boolean>;
  analytics?: {
    trackEvent?: (name: string, details?: Record<string, unknown>) => void;
  };
  open?: boolean;
  onClose: (
    reason: ActivityEditorCloseReason,
    info: ActivityEditorCloseInfo,
  ) => void;
  onExited?: () => void;
  onSwitchToLegacyView?: () => void;
  onToast?: (event: ActivityEditorToastEvent) => void;
};

type ActivityEditorModule = {
  default: ComponentType<ActivityEditorRemoteProps>;
};

type Exit = 'closed' | 'legacy';

// Flag names the editor reads (resolveActivityEditorFeatureFlags + lib usage); all but the hardcoded
// isAgentActivityArtifactEnabled come 1:1 from wbConfig.
const featureFlags = () => ({
  isAgentActivityArtifactEnabled: false,
  isUniformRelationsEnabled: getFeatureFlag('isUniformRelationsEnabled'),
  isKpisEnabled: getFeatureFlag('isKpisEnabled'),
  isKpiRelationshipsEnabled: getFeatureFlag('isKpiRelationshipsEnabled'),
});

const currentUser = () => {
  const u = (window as any).uData ?? {};
  return {
    id: Number(u.user_id),
    fullname: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim(),
    profilePictureUrl: u.picture ?? '',
  };
};

/** Stands in until the remote dialog is on screen; its fade-out is the exit when no remote dialog ever mounts. */
const Loading = ({
  open,
  onExited,
  onClose,
}: {
  open: boolean;
  onExited: () => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Backdrop
      open={open}
      onExited={onExited}
      slotProps={{ root: { 'aria-hidden': false } }}
      sx={{ color: 'common.white', zIndex: 'modal' }}
    >
      <IconButton
        color="inherit"
        onClick={onClose}
        aria-label={t('activityEditor.legacyShell.close', 'Close')}
        data-testid="activity-editor-loading-close"
        sx={{ position: 'absolute', top: 16, right: 16 }}
      >
        <CloseIcon />
      </IconButton>
      <CircularProgress
        color="inherit"
        aria-label={t('activityEditor.legacyShell.loading', 'Loading Activity')}
      />
    </Backdrop>
  );
};

/** Runs an effect on mount only, so its placement in the tree decides when it runs. */
const OnMount = ({ run }: { run: () => void }) => {
  useEffect(run, [run]);
  return null;
};

// Passing it only makes the remote show "Use legacy view"; the cause arrives as onClose('switch-to-legacy').
const optIntoLegacyView = () => undefined;

let dismissOpenDialog: (() => void) | undefined;

export const FederatedActivityDialog = ({
  activityId,
  onExited,
  onChanged,
}: {
  activityId: string;
  onExited: (exit: Exit) => void;
  /** Fires just before onExited when the remote reported an edit or a delete. */
  onChanged?: (change: 'edited' | 'deleted') => void;
}) => {
  const [open, setOpen] = useState(true);
  const [RemoteDialog, setRemoteDialog] =
    useState<ActivityEditorModule['default']>();
  const exit = useRef<Exit>();
  const opened = useRef(false);
  const closed = useRef(false);
  const exited = useRef(false);
  const change = useRef<'edited' | 'deleted'>();
  const analytics = useMemo(
    () => activityEditorAnalytics(activityId),
    [activityId],
  );

  // The first cause wins, so a late load failure cannot reroute a user's dismiss to legacy.
  const settle = useCallback((how: Exit) => {
    if (!exit.current) exit.current = how;
  }, []);
  const dismiss = useCallback(() => {
    settle('closed');
    setOpen(false);
  }, [settle]);
  const fallBackToLegacy = useCallback(() => {
    settle('legacy');
    setOpen(false);
  }, [settle]);
  // The remote closes itself; this only records why, and the exit completes in handleExited.
  const handleClose = useCallback(
    (reason: ActivityEditorCloseReason, info?: ActivityEditorCloseInfo) => {
      if (exit.current) return;
      if (reason === 'deleted') change.current = 'deleted';
      else if (info?.wasEdited) change.current = 'edited';
      settle(reason === 'switch-to-legacy' ? 'legacy' : 'closed');
      if (reason === 'switch-to-legacy') {
        analytics.trackEvent('ARTIFACT', {
          action: 'ARTIFACT_INTERACTION',
          clickedOnElement: 'USE_LEGACY_VIEW',
        });
      }
    },
    [analytics, settle],
  );

  useEffect(() => {
    let active = true;
    // Loaded per open: a module-scoped lazy would replay one rejected load for the page's life.
    loadFederatedModule<ActivityEditorModule>(
      'WorkstreamsUI/ActivityEditorDialog',
    )
      // A dismiss that landed mid-load leaves the loading backdrop to fade out as the exit.
      .then(
        (module) =>
          active && !exit.current && setRemoteDialog(() => module.default),
      )
      .catch(() => active && fallBackToLegacy());
    return () => {
      active = false;
    };
  }, [fallBackToLegacy]);

  const trackOpen = useCallback(() => {
    if (opened.current) return;
    opened.current = true;
    analytics.trackEvent('ARTIFACT', { action: 'OPEN_ARTIFACT' });
  }, [analytics]);

  /** Every editor the user saw gets exactly one close, however it ended. */
  const trackClose = useCallback(() => {
    if (!opened.current || closed.current) return;
    closed.current = true;
    analytics.trackEvent('ARTIFACT', { action: 'CLOSE_ARTIFACT' });
  }, [analytics]);

  // Catches a teardown that skips the exit transition, such as a second
  // openFederatedActivityDialog unmounting this root out from under it.
  useEffect(() => trackClose, [trackClose]);

  // Legacy popup mounts only after the fade-out, so the two surfaces never stack.
  const handleExited = useCallback(() => {
    if (exited.current) return;
    exited.current = true;
    // Before onExited, which may navigate the page away mid-flight.
    trackClose();
    if (change.current) onChanged?.(change.current);
    onExited(exit.current ?? 'closed');
  }, [trackClose, onChanged, onExited]);

  useEffect(() => {
    if (!open) return undefined;
    dismissOpenDialog = dismiss;
    return () => {
      if (dismissOpenDialog === dismiss) dismissOpenDialog = undefined;
    };
  }, [open, dismiss]);

  return (
    <ErrorBoundary
      renderError={() => (
        <>
          <OnMount run={fallBackToLegacy} />
          <Loading open={open} onExited={handleExited} onClose={dismiss} />
        </>
      )}
    >
      <Suspense
        fallback={
          <Loading open={open} onExited={handleExited} onClose={dismiss} />
        }
      >
        {RemoteDialog ? (
          <>
            {/* Inside the boundary, so an editor that throws on its first render never
                mounts this and reports no open to go unmatched by a close. */}
            <OnMount run={trackOpen} />
            <RemoteDialog
              activityId={activityId}
              baseApiUrl={window.location.origin}
              currentUser={currentUser()}
              featureFlags={featureFlags()}
              analytics={analytics}
              open={open}
              onClose={handleClose}
              onExited={handleExited}
              onSwitchToLegacyView={optIntoLegacyView}
              onToast={showActivityEditorToast}
            />
          </>
        ) : (
          <Loading open={open} onExited={handleExited} onClose={dismiss} />
        )}
      </Suspense>
    </ErrorBoundary>
  );
};

/** Dismisses an open federated dialog; false when none is open. */
export const dismissFederatedActivityDialog = () => {
  if (!dismissOpenDialog) return false;
  dismissOpenDialog();
  return true;
};

let dialogRoot: ReactDOM.Root | undefined;
let releaseDialogOpen: (() => void) | undefined;

export const openFederatedActivityDialog = (
  activityId: string,
  {
    onClosed,
    onSwitchToLegacyView,
  }: {
    onClosed: (info: ActivityEditorCloseInfo & { deleted: boolean }) => void;
    onSwitchToLegacyView: (wasEdited: boolean) => void;
  },
) => {
  let el = document.getElementById('aev2-dialog-container');
  if (!el) {
    el = document.createElement('DIV');
    el.setAttribute('id', 'aev2-dialog-container');
    document.body.appendChild(el);
  }
  dialogRoot?.unmount();
  releaseDialogOpen?.();
  const release = holdActivityOpen();
  releaseDialogOpen = release;
  const root = ReactDOM.createRoot(el);
  dialogRoot = root;
  let change: 'edited' | 'deleted' | undefined;
  root.render(
    <FederatedActivityDialog
      activityId={activityId}
      onChanged={(c) => {
        change = c;
      }}
      onExited={(exit) => {
        // Defer: unmounting a root synchronously from inside its own render callback warns in React 18.
        setTimeout(() => root.unmount());
        if (dialogRoot === root) dialogRoot = undefined;
        if (exit === 'legacy') onSwitchToLegacyView(change === 'edited');
        else
          onClosed({
            wasEdited: Boolean(change),
            deleted: change === 'deleted',
          });
        // After the switch, so the legacy popup's hold overlaps this one and focus never re-arms between them.
        release();
      }}
    />,
  );
};
